import { useField, useFormikContext } from 'formik';
import React, { useRef } from 'react';

import styled from '@emotion/styled';

interface OTPInputFieldProps {
  label: string;
  name: string;
  className?: string;
}

const OTPInputField: React.FC<OTPInputFieldProps> = ({
  label,
  name,
  className,
}) => {
  const { setFieldValue } = useFormikContext<any>();
  const [field, meta] = useField(name);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const size = 6;
    const { value } = e.target;

    if (value.length > 1 && /^\d+$/.test(value)) {
      const digits = value.split('').slice(0, size);
      const currentOtp = field.value.split('');

      digits.forEach((digit, i) => {
        if (index + i < size) {
          currentOtp[index + i] = digit;
        }
      });

      setFieldValue(name, currentOtp.join(''));

      const nextFocusIndex = Math.min(index + digits.length, size - 1);
      inputsRef.current[nextFocusIndex]?.focus();
    } else if (/^[0-9]$/.test(value)) {
      const otp = field.value.split('');
      otp[index] = value;
      setFieldValue(name, otp.join(''));

      if (index < size - 1 && value) {
        inputsRef.current[index + 1]?.focus();
      }
    } else if (value === '') {
      const otp = field.value.split('');
      otp[index] = '';
      setFieldValue(name, otp.join(''));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace') {
      const otp = field.value.split('');
      if (otp[index] === '') {
        if (index > 0) {
          inputsRef.current[index - 1]?.focus();
          const prevOtp = field.value.split('');
          prevOtp[index - 1] = '';
          setFieldValue(name, prevOtp.join(''));
        }
      } else {
        otp[index] = '';
        setFieldValue(name, otp.join(''));
        // this condition ensures that focus only moves back
        // if the current input is empty after deletion
        if (index > 0) {
          inputsRef.current[index - 1]?.focus();
        }
      }
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const size = 6;
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');

    if (/^\d{1,6}$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, size);
      const currentOtp = field.value.split('');

      digits.forEach((digit, i) => {
        if (index + i < size) {
          currentOtp[index + i] = digit;
        }
      });

      setFieldValue(name, currentOtp.join(''));

      const nextFocusIndex = Math.min(index + digits.length, size - 1);
      inputsRef.current[nextFocusIndex]?.focus();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <InputWrapper className={className}>
      <InputLabel>{label}</InputLabel>
      <OTPContainer>
        {Array.from({ length: 6 }).map((_, index) => (
          <OTPInput
            key={index}
            type='text'
            maxLength={1}
            value={field.value[index] || ''}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            onFocus={handleFocus}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
          />
        ))}
      </OTPContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default OTPInputField;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const InputLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: black;
`;

const OTPContainer = styled.div`
  display: flex;
  gap: 8px;
  width: fit-content;
`;

const OTPInput = styled.input`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 10px;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
  width: 40px;
  height: 40px;
  color: ${({ theme }) => theme.palette.logirateBlack};

  &:focus {
    outline: 1px solid ${({ theme }) => theme.palette.logiratePrimary};
    border: none;
  }
`;

const FieldErrorInfo = styled.div`
  font-size: 12px;
  color: red;
  margin-top: -10px;
`;
